import { FailureStreak, sleepWhile } from './failure-backoff.js';
import { playbackErrorCode, playbackErrorMessage, } from './playlist-player.js';
import { wallTimelinePositionSeconds } from './wall-timeline.js';
import { WALL_DRIFT_CHECK_MS, WALL_DRIFT_MIN_SEEK_INTERVAL_MS, WALL_DRIFT_SEEK_LANDING_TOLERANCE_SECONDS, WALL_DRIFT_SEEK_SECONDS, wallDriftAction, wallDriftErrorSeconds, } from './wall-drift.js';
import { consoleLogger } from '../ports/logger.js';
function yieldEventLoop() {
    return new Promise((resolve) => setTimeout(resolve, 0));
}
function toBlobUri(bytes, mimeType) {
    if (typeof URL !== 'undefined' &&
        typeof Blob !== 'undefined' &&
        typeof URL.createObjectURL === 'function') {
        return URL.createObjectURL(new Blob([bytes], { type: mimeType }));
    }
    return `memory://${crypto.randomUUID()}`;
}
/** Wait until the server clock reaches `targetMs` (returns at once if it already has). */
function waitForServerTime(targetMs, serverNow) {
    const wait = Math.max(0, targetMs - serverNow());
    return new Promise((resolve) => setTimeout(resolve, wait));
}
/** Old callers pass the raw sync `serverTime`: take its offset now, before any download. */
function serverNowFromServerTime(serverTime) {
    const offsetMs = serverTime ? Date.parse(serverTime) - Date.now() : 0;
    const safeOffset = Number.isFinite(offsetMs) ? offsetMs : 0;
    return () => Date.now() + safeOffset;
}
/**
 * Plays a wall tile: wait for cloud epochStartAt, then loop the tile (fill + mute).
 */
export class WallPlayer {
    engine;
    assets;
    hasher;
    options;
    stopped = true;
    logger;
    activeKey = null;
    constructor(engine, assets, hasher, logger, options = {}) {
        this.engine = engine;
        this.assets = assets;
        this.hasher = hasher;
        this.options = options;
        this.logger = logger ?? consoleLogger;
    }
    /**
     * While a tile plays, compare its position with the shared timeline about twice a second.
     * Followers get a small speed nudge; any tile far off gets a hard seek, aimed ahead by
     * how long the last seek took so it doesn't land behind again.
     */
    startDriftCorrection(input) {
        const engine = this.engine;
        if (!engine.getPositionSeconds || !engine.seek || !(input.durationSeconds > 0)) {
            return { stop: () => undefined };
        }
        let previousError = null;
        let currentRate = 1;
        let seeking = false;
        let seekLeadSeconds = 0.15;
        // Seek-loop guard state.
        let lastSeekAtMs = Number.NEGATIVE_INFINITY;
        let awaitingLanding = false;
        let landedErrorSeconds = null;
        const timer = setInterval(() => {
            if (seeking)
                return;
            const position = engine.getPositionSeconds?.() ?? null;
            if (position === null)
                return;
            const want = wallTimelinePositionSeconds(input.serverNow(), input.epochMs, input.durationSeconds);
            const error = wallDriftErrorSeconds(position, want, input.durationSeconds);
            if (awaitingLanding) {
                // First reading after a seek: where it actually landed (a keyframe on Tizen).
                landedErrorSeconds = error;
                awaitingLanding = false;
            }
            else if (Math.abs(error) <= WALL_DRIFT_SEEK_SECONDS) {
                landedErrorSeconds = null;
            }
            const seekAllowed = Date.now() - lastSeekAtMs >= WALL_DRIFT_MIN_SEEK_INTERVAL_MS &&
                !(landedErrorSeconds !== null &&
                    Math.abs(error) <=
                        Math.abs(landedErrorSeconds) + WALL_DRIFT_SEEK_LANDING_TOLERANCE_SECONDS);
            const action = wallDriftAction(error, previousError, {
                canNudgeRate: input.canNudgeRate,
                canSeek: seekAllowed,
            });
            previousError = error;
            if (action.kind === 'seek') {
                seeking = true;
                previousError = null;
                lastSeekAtMs = Date.now();
                if (currentRate !== 1) {
                    engine.setPlaybackRate?.(1);
                    currentRate = 1;
                }
                const started = Date.now();
                const target = wallTimelinePositionSeconds(input.serverNow(), input.epochMs, input.durationSeconds) +
                    seekLeadSeconds;
                void engine.seek(target % input.durationSeconds)
                    .then(() => {
                    seekLeadSeconds = Math.min(0.5, Math.max(0, (Date.now() - started) / 1000));
                })
                    .finally(() => {
                    seeking = false;
                    awaitingLanding = true;
                });
                return;
            }
            if (action.rate !== currentRate) {
                engine.setPlaybackRate?.(action.rate);
                currentRate = action.rate;
            }
        }, this.options.driftCheckMs ?? WALL_DRIFT_CHECK_MS);
        return {
            stop: () => {
                clearInterval(timer);
                if (currentRate !== 1)
                    engine.setPlaybackRate?.(1);
            },
        };
    }
    getActiveKey() {
        return this.activeKey;
    }
    /**
     * Download, checksum and cache the tile. Doesn't touch the screen or need epochStartAt.
     * The runtime reports READY only after this succeeds: READY means "cached and ready to play".
     */
    async prepareAssignment(assignment, options) {
        const item = assignment.item;
        if (!item)
            throw new Error('Wall assignment has no tile to prepare');
        let stored = await this.assets.get(item.assetId);
        if (!stored || stored.sha256 !== item.sha256) {
            const bytes = await options.downloadBytes(item.url);
            const hash = await this.hasher.sha256Hex(bytes);
            if (hash !== item.sha256) {
                throw new Error(`Wall tile checksum mismatch for ${item.assetId}`);
            }
            await this.assets.put({
                assetId: item.assetId,
                bytes,
                mimeType: item.mimeType,
                sha256: item.sha256,
                localUri: toBlobUri(bytes, item.mimeType),
            });
            stored = await this.assets.get(item.assetId);
        }
        if (!stored)
            throw new Error(`Failed to cache wall tile ${item.assetId}`);
        return stored;
    }
    async playAssignment(assignment, options) {
        if (!assignment.item || !assignment.epochStartAt)
            return;
        const serverNow = options.serverNow ?? serverNowFromServerTime(options.serverTime);
        const key = `${assignment.publicationId}:${assignment.timelineId}:${assignment.item.sha256}`;
        this.stopped = false;
        this.activeKey = key;
        const item = assignment.item;
        const stored = await this.prepareAssignment(assignment, options);
        const epochMs = Date.parse(assignment.epochStartAt);
        await waitForServerTime(epochMs, serverNow);
        this.engine.setScaleMode?.('fill');
        this.engine.setRotationDegrees?.(0);
        this.engine.setMuted?.(assignment.muteAudio);
        const playable = {
            assetId: item.assetId,
            url: item.url,
            sha256: item.sha256,
            durationSeconds: item.durationSeconds,
            mimeType: item.mimeType,
            localUri: stored.localUri,
            resolveNetworkUrl: () => Promise.resolve(item.url),
        };
        const failures = new FailureStreak();
        while (!this.stopped && this.activeKey === key) {
            // Only the leader counts plays; followers report failures only.
            // Older servers may omit the source ID. Never send the synthetic tile ID.
            const base = {
                ...(assignment.sourceMediaAssetId ? { mediaAssetId: assignment.sourceMediaAssetId } : {}),
                itemPosition: 0,
            };
            const startedAt = performance.now();
            const startPlayedAt = new Date(serverNow()).toISOString();
            // During a failure streak the start is held back and only recorded if this attempt plays.
            const startDeferred = failures.failing;
            if (assignment.isLeader && !startDeferred) {
                this.options.playbackEvents?.record({
                    ...base,
                    eventType: 'play_start',
                    playedAt: startPlayedAt,
                });
            }
            try {
                if (this.engine.playAt) {
                    // Every pass (first start, late join, each loop) starts from the shared
                    // timeline instead of 0:00, so tiles can't drift apart loop by loop.
                    const drift = this.startDriftCorrection({
                        serverNow,
                        epochMs,
                        durationSeconds: item.durationSeconds ?? 0,
                        // Only muted followers change speed; the (0,0) tile has the sound and is the reference.
                        canNudgeRate: assignment.muteAudio,
                    });
                    try {
                        await this.engine.playAt(playable, (mediaDurationSeconds) => wallTimelinePositionSeconds(serverNow(), epochMs, 
                        // Every tile must use the same clip length, or they slip apart every loop.
                        item.durationSeconds && item.durationSeconds > 0
                            ? item.durationSeconds
                            : mediaDurationSeconds));
                    }
                    finally {
                        drift.stop();
                    }
                }
                else {
                    await this.engine.play(playable);
                }
                if (assignment.isLeader && startDeferred) {
                    this.options.playbackEvents?.record({
                        ...base,
                        eventType: 'play_start',
                        playedAt: startPlayedAt,
                    });
                }
                failures.succeed();
                if (assignment.isLeader) {
                    this.options.playbackEvents?.record({
                        ...base,
                        eventType: this.stopped || this.activeKey !== key ? 'play_interrupted' : 'play_end',
                        playedAt: new Date(serverNow()).toISOString(),
                        durationMs: Math.max(0, Math.round(performance.now() - startedAt)),
                    });
                }
            }
            catch (error) {
                // One play_failed per failure streak, not one per attempt.
                if (failures.fail()) {
                    this.logger.warn('Wall tile playback failed', {
                        assetId: item.assetId,
                        error: error instanceof Error ? error.message : String(error),
                    });
                    this.options.playbackEvents?.record({
                        ...base,
                        eventType: 'play_failed',
                        playedAt: new Date(serverNow()).toISOString(),
                        errorCode: playbackErrorCode(error),
                        errorMessage: playbackErrorMessage(error),
                    });
                }
            }
            if (this.stopped || this.activeKey !== key)
                return;
            if (failures.failing) {
                // Back off (5 s doubling to 60 s) instead of retrying instantly.
                await sleepWhile(failures.backoffMs(), () => !this.stopped && this.activeKey === key, this.options.failureSleep);
            }
            else {
                await yieldEventLoop();
            }
        }
    }
    async stop() {
        const wasActive = this.activeKey !== null;
        this.stopped = true;
        this.activeKey = null;
        // No wall running (normal playlist screen): leave the shared engine alone.
        // The runtime calls this on every sync without a wall assignment; stopping
        // the engine here would cut the playlist's current video short every 30s.
        if (!wasActive)
            return;
        this.engine.setMuted?.(false);
        await this.engine.stop();
    }
}
//# sourceMappingURL=wall-player.js.map